import * as React from "react"
import {
  type EntityCategory,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "@digits-graphql/frontend/graphql-bearer"
import { SvgAlertTriangleSolid } from "@digits-shared/components/SVGIcons/solid/AlertTriangleSolid.svg"
import { SvgCheckCircleSolid } from "@digits-shared/components/SVGIcons/solid/CheckCircleSolid.svg"
import useSession from "@digits-shared/hooks/useSession"
import { useMolecule } from "bunshi/react"
import { useAtomValue } from "jotai"
import { newCategoryModalMolecule } from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/NewCategoryModal/newCategoryModalMolecule"
import { useChromeAlertContext } from "src/frontend/components/Shared/Alerts/ChromeAlertsContext"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import type FrontendSession from "src/frontend/session"
import { isCategory } from "src/shared/components/Typeahead/sharedTypeahead"

export function useCreateOrUpdateCategory(
  onClose: (categoryId?: string) => void,
  onSuccess: (entity: EntityCategory) => void
) {
  const { shouldUpdateCategoryAtom } = useMolecule(newCategoryModalMolecule)
  const shouldUpdateCategory = useAtomValue(shouldUpdateCategoryAtom)
  const { createCategory, loading: createSubmitting } = useCreateCategory(onClose, onSuccess)
  const { updateCategory, loading: updateSubmitting } = useUpdateCategory(onClose, onSuccess)

  return React.useMemo(
    () =>
      shouldUpdateCategory
        ? {
            createOrUpdateCategory: updateCategory,
            submitting: updateSubmitting,
          }
        : {
            createOrUpdateCategory: createCategory,
            submitting: createSubmitting,
          },
    [shouldUpdateCategory, updateCategory, updateSubmitting, createCategory, createSubmitting]
  )
}

function useCreateCategory(
  onClose: (categoryId?: string) => void,
  onSuccess: (entity: EntityCategory) => void
) {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const { addAlert } = useChromeAlertContext()
  const [createCategory, { loading }] = useCreateCategoryMutation()

  const {
    categoryNameAtom,
    completeCategoryNumberAtom,
    descriptionAtom,
    parentCategoryAtom,
    subtypeAtom,
    iconDisplayKeyAtom,
  } = useMolecule(newCategoryModalMolecule)

  const categoryName = useAtomValue(categoryNameAtom)
  const chartOfAccountsDisplayNumber = useAtomValue(completeCategoryNumberAtom)
  const description = useAtomValue(descriptionAtom)
  const parentCategory = useAtomValue(parentCategoryAtom)
  const subtype = useAtomValue(subtypeAtom)
  const iconDisplayKey = useAtomValue(iconDisplayKeyAtom)

  return {
    loading,
    createCategory: React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        if (!subtype || !parentCategory || !categoryName) return

        return createCategory({
          variables: {
            legalEntityId,
            category: {
              name: categoryName,
              chartOfAccountsDisplayNumber,
              description,
              parentCategoryId: parentCategory.id,
              type: parentCategory.type,
              displayKey: iconDisplayKey || parentCategory.displayKey,
              subtype,
            },
          },
        })
          .then((res) => {
            const categoryId = res.data?.createCategory?.categoryId
            onClose(categoryId)

            if (categoryId) {
              onSuccess({
                id: categoryId,
                name: categoryName,
                chartOfAccountsDisplayNumber,
                parentCategoryId: parentCategory.id,
                type: parentCategory.type,
                displayKey: iconDisplayKey || parentCategory.displayKey,
                subtype,
                active: true,
              })
            }

            addAlert({
              id: "new-category-success",
              message: "Category created",
              SVGComponent: SvgCheckCircleSolid,
              autoDismiss: true,
            })
          })
          .catch(() => {
            addAlert({
              id: "new-category-error",
              message: "Unable to create new category",
              SVGComponent: SvgAlertTriangleSolid,
              autoDismiss: true,
            })
          })
      },
      [
        subtype,
        parentCategory,
        categoryName,
        createCategory,
        legalEntityId,
        chartOfAccountsDisplayNumber,
        description,
        iconDisplayKey,
        onClose,
        addAlert,
        onSuccess,
      ]
    ),
  }
}

function useUpdateCategory(
  onClose: (categoryId?: string) => void,
  onSuccess: (entity: EntityCategory) => void
) {
  const { currentLegalEntityId: legalEntityId } = useSession<FrontendSession>()
  const { addAlert } = useChromeAlertContext()
  const viewKey = useViewVersion()
  const [updateCategory, { loading }] = useUpdateCategoryMutation()

  const {
    categoryNameAtom,
    completeCategoryNumberAtom,
    descriptionAtom,
    parentCategoryAtom,
    subtypeAtom,
    iconDisplayKeyAtom,
    initialCategoryAtom,
  } = useMolecule(newCategoryModalMolecule)

  const categoryName = useAtomValue(categoryNameAtom)
  const chartOfAccountsDisplayNumber = useAtomValue(completeCategoryNumberAtom)
  const description = useAtomValue(descriptionAtom)
  const parentCategory = useAtomValue(parentCategoryAtom)
  const subtype = useAtomValue(subtypeAtom)
  const iconDisplayKey = useAtomValue(iconDisplayKeyAtom)
  const initialCategory = useAtomValue(initialCategoryAtom)

  return {
    loading,
    updateCategory: React.useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation()
        const isCategoryType = isCategory(initialCategory)
        if (!categoryName || !legalEntityId || !parentCategory || !subtype || !isCategoryType)
          return

        updateCategory({
          variables: {
            legalEntityId,
            viewKey,
            category: {
              categoryId: initialCategory.id,
              name: categoryName,
              chartOfAccountsDisplayNumber: chartOfAccountsDisplayNumber || "",
              displayKey: iconDisplayKey || parentCategory.displayKey,
              description: description || "",
              parentCategoryId: parentCategory.id,
              type: parentCategory.type,
              subtype,
            },
          },
        })
          .then((res) => {
            onClose(initialCategory.id)

            onSuccess({
              id: initialCategory.id,
              name: categoryName,
              chartOfAccountsDisplayNumber,
              parentCategoryId: parentCategory.id,
              type: parentCategory.type,
              displayKey: iconDisplayKey || parentCategory.displayKey,
              subtype,
              active: true,
            })

            addAlert({
              id: "new-category-update-success",
              message: "Category created",
              SVGComponent: SvgCheckCircleSolid,
              autoDismiss: true,
            })
          })
          .catch(() => {
            addAlert({
              id: "new-category-update-error",
              message: "Unable to create new category",
              SVGComponent: SvgAlertTriangleSolid,
              autoDismiss: true,
            })
          })
      },
      [
        addAlert,
        categoryName,
        chartOfAccountsDisplayNumber,
        description,
        iconDisplayKey,
        initialCategory,
        legalEntityId,
        onClose,
        onSuccess,
        parentCategory,
        subtype,
        updateCategory,
        viewKey,
      ]
    ),
  }
}
